/* ============================================================================
   MEDIA-SURE — ninguna imagen rota delante de un cliente
   ----------------------------------------------------------------------------
   El icono de imagen rota del navegador es la tripa del entorno asomando: dice
   « aquí falló algo » con la tipografía de Chrome, dentro de una tienda que se
   presenta como cuidada. En el Studio había tres a la vez en el mug —el
   mockup, la textura y la miniatura de VUE— por dos motivos distintos y los
   dos triviales:

     · <img> escrito en el HTML SIN atributo `src`. Un img sin src no es un
       img vacío: el navegador resuelve la URL del documento, se la baja, ve
       que es HTML y pinta el icono de rotura.
     · plantillas que hacen src="${p.image || ''}". La cadena vacía hace
       exactamente lo mismo.

   Aquí no se arregla cada caso: se cierra la clase entera. Cualquier imagen
   sin fuente utilizable —ahora o dentro de un año, escrita a mano o generada—
   se queda en un plano neutro controlado, y se marca para que quien la mire
   sepa que le falta el asset.

   No sustituye a tener la fotografía. Sustituye a enseñar que no la hay.
   ========================================================================== */

(() => {
  'use strict';

  // Un píxel transparente. Es una fuente VÁLIDA, así que el navegador no
  // vuelve a intentar nada ni dispara otro error.
  const VACIO = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg"/%3E';

  /** ¿Esta fuente va a acabar en el icono de rotura? */
  function fuenteInutil(img) {
    const bruto = img.getAttribute('src');
    if (bruto === null || bruto.trim() === '') return true;
    // src="?produit=..." o src="#": resuelven al propio documento, que es
    // HTML. Es el caso que se veía en el mug.
    try {
      const u = new URL(bruto, location.href);
      return u.pathname === location.pathname && u.origin === location.origin
             && !/\.(png|jpe?g|webp|avif|gif|svg)$/i.test(u.pathname);
    } catch (e) { return true; }
  }

  function neutralizar(img) {
    if (img.dataset.imgFallback === 'true') return;
    img.dataset.imgFallback = 'true';
    img.src = VACIO;
    // Sin alt, un lector de pantalla anunciaría « imagen » y nada más.
    if (!img.alt) img.setAttribute('role', 'presentation');
  }

  function revisar(raiz = document) {
    for (const img of raiz.querySelectorAll ? raiz.querySelectorAll('img') : []) {
      if (fuenteInutil(img)) neutralizar(img);
    }
  }

  /* Los errores de carga no burbujean, pero sí se capturan. Un solo oyente en
     la raíz cubre las imágenes que todavía no existen. */
  document.addEventListener('error', (e) => {
    const t = e.target;
    if (t && t.tagName === 'IMG' && t.dataset.imgFallback !== 'true') neutralizar(t);
  }, true);

  /* Y las que se añaden después: el Studio pinta miniaturas de vista, de
     plantilla y de foto a medida que el cliente trabaja. */
  if ('MutationObserver' in window) {
    new MutationObserver((muts) => {
      for (const m of muts) {
        for (const n of m.addedNodes) {
          if (n.nodeType !== 1) continue;
          if (n.tagName === 'IMG' && fuenteInutil(n)) neutralizar(n);
          else revisar(n);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => revisar());
  } else {
    revisar();
  }

  window.MEDIA_SURE = { revisar, neutralizar, fuenteInutil };
})();

/* ----------------------------------------------------------------------------
   Estado controlado cuando la vista no tiene mockup

   El mug publicado tiene `mockup: ""` en su única vista: no existe la
   fotografía del objeto. Sin ella, la vista previa 3D no tiene nada que
   texturizar y la ayuda « Tournez la tasse avec le doigt » promete un gesto
   que no puede ocurrir. Una promesa que la página no cumple es peor que un
   hueco declarado.

   Esto NO inventa un mug. Dice lo que hay: el diseño se puede componer y la
   vista del objeto llegará con su fotografía.
   -------------------------------------------------------------------------- */
(() => {
  'use strict';

  function hayObjeto() {
    const lienzo = document.querySelector('#apercu3d canvas, .apercu3d canvas');
    if (lienzo && lienzo.width > 1) return true;
    const mk = document.querySelector('#mockup');
    return Boolean(mk && mk.dataset.imgFallback !== 'true' && mk.naturalWidth > 1);
  }

  function ajustar() {
    if (hayObjeto()) return;
    for (const sel of ['.apercu3d__aide', '.apercu-grand__aide']) {
      for (const el of document.querySelectorAll(sel)) {
        el.textContent = 'Aperçu de l’objet bientôt disponible. '
                       + 'Votre création se compose normalement.';
        el.dataset.sansObjet = 'true';
      }
    }
  }

  // Tras el arranque del visor 3D, que es asíncrono.
  if (document.readyState === 'complete') setTimeout(ajustar, 1200);
  else window.addEventListener('load', () => setTimeout(ajustar, 1200));
})();
